import Block from '@/components/Block'
import EmptyBlock from '@/components/EmptyBlock'
import GsuLoader from '@/components/GsuLoader'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { coreLayoutSectionX } from '@/constants/measured-scope'
import { useUI } from '@/context/ui'
import useVisibility from '@/hooks/useVisibility'
import { PageInfo, ScheduleDaySection, SessionCard } from '@/shared/api-generated-types'
import CardViewClass from '@/widgets/CardViewClass'
import dayjs from 'dayjs'
import throttle from 'lodash/throttle'
import uniqBy from 'lodash/uniqBy'
import React, { useEffect, useMemo, useState } from 'react'
import { SectionIntersectionObserver } from './SectionIntersectionObserver'
import * as styles from './style'

export interface ScheduleBlockProps {
  items: ScheduleDaySection[]
  className?: string
  onLoadMore?: (idx: number, page: number) => void
  pageInfo?: Partial<PageInfo>
  fetching?: boolean
  // Mostly used to indicate core data criteria has changed and to purge the old render results
  distinction?: string
}

interface Data {
  date?: string
  items: Array<SessionCard>
}

const ScheduleBlock: React.FC<ScheduleBlockProps> = ({
  items: daySections,
  className = '',
  onLoadMore: _onLoadMore,
  fetching,
  pageInfo,
}) => {
  const [data, setData] = useState<Array<Data>>([])
  const {
    state: { schedulePage },
  } = useUI()

  const { reset, index } = schedulePage

  // Wipe existing day sections and allow data to be re-calculated since the
  // filter has changed, we can't be certain that the data we have is still valid
  useEffect(() => {
    if (reset) {
      setData([])
    }
  }, [reset])

  // Pagination requirements
  const onLoadMore = _onLoadMore ? throttle(_onLoadMore, 2500) : undefined
  const isLoadMoreAvailable = onLoadMore && pageInfo?.hasNextPage
  const [isLoadMoreVisible, loadMoreRef] = useVisibility()

  useEffect(() => {
    if (isLoadMoreAvailable && isLoadMoreVisible && !fetching && !!pageInfo) {
      onLoadMore(0, pageInfo?.page ? pageInfo.page + 1 : 1)
    }
  }, [isLoadMoreVisible])

  /**
   * As daySection data changes, we maintain the accumulated data set which
   * the client will be using to control rendering of the schedule.
   */
  useEffect(() => {
    // Do not process empty results in this manner
    if (!daySections?.length) return

    //If the backend returns sessions for days before the activeDate
    // This wee bit of code filters out those addl sessions.
    const arr = [...daySections]
    const chosenDate = dayjs(schedulePage.day || new Date())
      .subtract(1, 'day')
      .format('YYYY-MM-DD')
    const sectionDate = dayjs(daySections[0].date).format('YYYY-MM-DD')

    if (chosenDate === sectionDate) {
      arr.shift()
    }

    // Reference the first date inside response data
    const firstDateFromResponse = arr[0]?.date
    // The index of the last populated day section
    const prevLastIndex = data.length ? data.length - 1 : 0
    // Check the date of the last populated day section
    const lastPopulatedDate = data[prevLastIndex]?.date
    // Where the response data starts inside a date our client finished on
    // then we'll be populating more of that day data, thus 'partial day'
    const hasPartialDay = firstDateFromResponse === lastPopulatedDate

    if (hasPartialDay) {
      setData((prev) => {
        // All day sections prior to the partial day that must be processed
        const oldSections = prev.length > 1 ? prev.slice(0, prev.length - 1) : []
        // Subset of day sections that is only the partial day
        const updates = prev.length > 1 ? prev.slice(prevLastIndex, prev.length) : [...prev]
        // Any day sections from the response that are not already accounted for on the client
        const newSections = arr.length > 1 ? arr.slice(1) : []
        if (index > 0) {
          try {
            // Blend items for the overlapped day, unique by session ID
            updates[0].items = uniqBy([...updates[0]?.items, ...arr[0].items], 'id')
          } catch (error) {
            console.log('Issue blending partial day updates', { error })
          }
          return [...oldSections, ...updates, ...newSections]
        }
        return [...arr]
      })
    } else {
      // No partial day to worry about, all new day sections
      setData([...arr])
    }
    if (index === 0) {
      setTimeout(() => {
        // We set a half second delayed scroll to top,
        // to ensure when the user receives the data,
        // They view it from the top of the list.
        if (typeof window !== 'undefined') {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          })
        }
      }, 200)
    }
  }, [daySections])

  const scheduleChunks = useMemo(() => {
    if (data.length === 0 && !fetching) {
      return (
        <EmptyBlock>
          <Text tag="h3" size="sm" weight="sm" className="ta-c p-sm">
            No classes available for chosen date
          </Text>
        </EmptyBlock>
      )
    }

    return data.map(({ date, items: cards }, index) => {
      return (
        <div className={styles.root} key={date} data-testid="scheduleSection">
          {dayjs(schedulePage.dayString).format('YYYY-MM-DD') !== dayjs(date).format('YYYY-MM-DD') ? (
            <>
              <Text
                tag="h2"
                size="md"
                weight="lg"
                responsiveSize={{ desktop: 'xl' }}
                className={`fc-dark pb-sm fw-xb-gte-desktop pt-lg`}
              >
                {dayjs(date).format('ddd D MMM')}
              </Text>
              <SectionIntersectionObserver
                isMobile
                position="start"
                sectionId={`top-${dayjs(date).format('YYYY-MM-DD')}`}
                triggerValue={dayjs(date).format('YYYY-MM-DD')}
              />
            </>
          ) : null}
          <Layout spacing="sm">
            {typeof cards !== 'string' && cards?.length && !fetching ? (
              <Stack y className={`${styles.cards} cardsWrapper`}>
                {cards.map((card) => (
                  <CardViewClass {...card} />
                ))}
              </Stack>
            ) : (
              <EmptyBlock>
                <Text tag="h3" size="sm" weight="sm" className="ta-c">
                  No classes available
                </Text>
              </EmptyBlock>
            )}
            <SectionIntersectionObserver
              isMobile
              position="end"
              sectionId={`bottom-${dayjs(date).format('YYYY-MM-DD')}`}
              triggerValue={dayjs(date).format('YYYY-MM-DD')}
            />
          </Layout>
        </div>
      )
    })
  }, [data])

  return (
    <section className={coreLayoutSectionX} id="schedule_block">
      {fetching && schedulePage.index === 0 ? (
        <div className={styles.loader}>
          <GsuLoader consumeSpace />
        </div>
      ) : (
        <Block className={`${className} p-0`}>
          {scheduleChunks}
          {isLoadMoreAvailable ? (
            <div
              id="schedule-block-end"
              className="flex-center justify-center py-lg"
              ref={loadMoreRef as React.MutableRefObject<HTMLDivElement>}
            >
              <GsuLoader />
            </div>
          ) : null}
        </Block>
      )}
    </section>
  )
}

export default ScheduleBlock
